/**
 * AgentStatusWidget — real-time agent pool status indicator (COORD-16e + AGENT-1 + PATCH-13 + INTEL-4).
 *
 * Persistent bottom-right widget showing agent pool activity.
 * Polls GET /api/agent-status every 5 seconds for pool stats.
 * Registry tab shows registered agents with trust levels and circuit breaker state.
 * Background tab shows 11 background task loops with recency bars and status.
 *
 * PATCH-13: Animated pool rows with spinning progress rings, throughput bars,
 * task descriptions, and queue drain ETA.
 *
 * INTEL-4: Background tab — all 11 background tasks with activity indicators,
 * recency bars, last-run descriptions, durations, and status dots.
 *
 * Collapsed: pill with total active agent count + color indicator.
 * Expanded: tabbed view — Pools | Registry | Background.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";

const POOL_LABELS = {
  screening: { label: "Screening", icon: "SA", task: "Classifying solicitations" },
  intel: { label: "Intel", icon: "IA", task: "Gathering OSINT intelligence" },
  fpds: { label: "FPDS", icon: "FA", task: "Researching procurement history" },
  brief: { label: "Brief", icon: "BA", task: "Compiling intelligence briefs" },
  forecast: { label: "Forecast", icon: "FE", task: "Evaluating agency forecasts" },
  response: { label: "Response", icon: "RA", task: "Building response documents" },
  actionables: { label: "Actions", icon: "AA", task: "Generating daily actionables" },
};

const POOL_ORDER = ["screening", "intel", "fpds", "brief", "forecast", "response", "actionables"];

const BG_TASK_LABELS = {
  "BG-COLLECT": "Intel Collection",
  "BG-ENRICH": "NER Enrichment",
  "BG-SCORE": "Relevance Scoring",
  "BG-BRIEF": "Morning Brief",
  "BG-TALENT": "Key Talent Scan",
  "BG-REAPER": "Data Retention",
  "BG-MARKET": "Market Intel",
  "BG-SPEC": "Deep Research",
  "BG-ENFORCE": "Task Enforcement",
  "BG-CROSSREF": "Cross-Reference",
  "BG-ACTION": "Daily Actionables",
};

// ── Background task metadata (INTEL-4) ──────────────────────────────
const BG_TASK_META = {
  "BG-COLLECT": { label: "Intel Collection",  icon: "\u{1F4E1}", color: "text-blue-700 bg-blue-50" },
  "BG-ENRICH":  { label: "NER Enrichment",    icon: "\u{1F9E0}", color: "text-purple-700 bg-purple-50" },
  "BG-SCORE":   { label: "Relevance Scoring", icon: "\u{1F4CA}", color: "text-indigo-700 bg-indigo-50" },
  "BG-BRIEF":   { label: "Morning Brief",     icon: "\u{1F4CB}", color: "text-amber-700 bg-amber-50" },
  "BG-TALENT":  { label: "Key Talent Scan",   icon: "\u{1F3C6}", color: "text-yellow-700 bg-yellow-50" },
  "BG-REAPER":  { label: "Data Retention",    icon: "\u{1F9F9}", color: "text-gray-700 bg-gray-100" },
  "BG-MARKET":  { label: "Market Intel",      icon: "\u{1F310}", color: "text-teal-700 bg-teal-50" },
  "BG-SPEC":    { label: "Deep Research",     icon: "\u{1F52C}", color: "text-cyan-700 bg-cyan-50" },
  "BG-ENFORCE": { label: "Task Enforcement",  icon: "\u26A1",    color: "text-orange-700 bg-orange-50" },
  "BG-CROSSREF":{ label: "Cross-Reference",   icon: "\u{1F517}", color: "text-rose-700 bg-rose-50" },
  "BG-ACTION":  { label: "Daily Actionables", icon: "\u2705",    color: "text-green-700 bg-green-50" },
};

const BG_TASK_ORDER = [
  "BG-COLLECT", "BG-ENRICH", "BG-SCORE", "BG-BRIEF", "BG-TALENT",
  "BG-REAPER", "BG-MARKET", "BG-SPEC", "BG-ENFORCE", "BG-CROSSREF", "BG-ACTION",
];

function groupByLatestTick(events) {
  const map = {};
  for (const ev of events) {
    if (!map[ev.agent_id]) map[ev.agent_id] = ev;
  }
  return map;
}

function formatBgDuration(ms) {
  if (ms == null) return "--";
  if (ms < 1000) return "<1s";
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.ceil((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

function recencyPct(dateStr) {
  if (!dateStr) return 0;
  const ageMin = (Date.now() - new Date(dateStr).getTime()) / 60000;
  if (ageMin < 1) return 100;
  if (ageMin >= 60) return 5;
  return Math.max(5, Math.round(100 - (ageMin / 60) * 95));
}

function bgTimeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getStatusColor(status) {
  if (!status || !status.enabled) return "gray";
  const totals = status.totals || {};
  const pools = status.pools || {};
  if (totals.failed_last_5min > 0) return "red";
  const anyThrottled = Object.values(pools).some((p) => p.throttle_state === "throttled");
  if (anyThrottled) return "yellow";
  if (totals.active_agents > 0) return "blue";
  return "green";
}

const COLOR_CLASSES = {
  gray: "bg-gray-400",
  green: "bg-emerald-500",
  blue: "bg-blue-500",
  yellow: "bg-amber-500",
  red: "bg-red-500",
};

const PILL_CLASSES = {
  gray: "bg-gray-100 text-gray-600 border-gray-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  yellow: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
};

const TRUST_COLORS = {
  draft: "bg-gray-100 text-gray-600",
  supervised: "bg-amber-50 text-amber-700",
  autonomous: "bg-green-50 text-green-700",
};

const CB_DOT = {
  closed: "bg-emerald-500",
  open: "bg-red-500",
  half_open: "bg-amber-500",
};

// ── Animated spinning progress ring ─────────────────────────────────
function ProgressRing({ active, max, size = 32, strokeWidth = 3 }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = max > 0 ? active / max : 0;
  const offset = circumference * (1 - pct);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className={active > 0 ? "animate-[spin_3s_linear_infinite]" : ""}>
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-200"
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className={active > 0 ? "text-blue-500 transition-all duration-700" : "text-gray-300"}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {/* Center count */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-[9px] font-bold ${active > 0 ? "text-blue-600" : "text-gray-400"}`}>
          {active}
        </span>
      </div>
    </div>
  );
}

// ── Throughput bar — shows completed/5min as animated bar ───────────
function ThroughputBar({ completed, failed, maxRate = 150 }) {
  const pct = Math.min((completed / maxRate) * 100, 100);
  return (
    <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
      {completed > 0 && (
        <div
          className="h-full rounded-full bg-emerald-400 transition-all duration-1000 ease-out"
          style={{ width: `${pct}%` }}
        />
      )}
      {failed > 0 && (
        <div
          className="h-full rounded-full bg-red-400 transition-all duration-500"
          style={{ width: `${Math.min((failed / maxRate) * 100, 100)}%`, marginTop: -6 }}
        />
      )}
    </div>
  );
}

// ── Queue drain ETA ─────────────────────────────────────────────────
function formatETA(queueDepth, completedPer5Min) {
  if (queueDepth === 0) return null;
  if (completedPer5Min <= 0) return "stalled";
  const ratePerMin = completedPer5Min / 5;
  const minutesLeft = queueDepth / ratePerMin;
  if (minutesLeft < 1) return "<1m";
  if (minutesLeft < 60) return `~${Math.ceil(minutesLeft)}m`;
  const hours = Math.floor(minutesLeft / 60);
  const mins = Math.ceil(minutesLeft % 60);
  return `~${hours}h ${mins}m`;
}

// ── Pool row with animations ────────────────────────────────────────
function PoolRow({ poolName, pool }) {
  const meta = POOL_LABELS[poolName] || { label: poolName, icon: "??", task: "Processing" };
  const isActive = pool.active_agents > 0;
  const hasQueue = pool.queue_depth > 0;
  const hasFailures = pool.failed_last_5min > 0;
  const isThrottled = pool.throttle_state === "throttled";
  const totalProcessed = pool.total_completed + pool.total_failed;
  const eta = formatETA(pool.queue_depth, pool.completed_last_5min);

  // If pool has done nothing and isn't active, show minimal row
  if (!isActive && totalProcessed === 0 && pool.queue_depth === 0) {
    return (
      <div className="px-3 py-1.5 flex items-center gap-3 text-xs opacity-40">
        <ProgressRing active={0} max={pool.max_agents} size={28} strokeWidth={2.5} />
        <span className="font-medium text-gray-500 w-16 truncate">{meta.label}</span>
        <span className="text-gray-400 text-[10px]">idle</span>
      </div>
    );
  }

  return (
    <div className={`px-3 py-2.5 transition-colors duration-300 ${isActive ? "bg-blue-50/60" : ""}`}>
      <div className="flex items-center gap-3">
        {/* Spinning progress ring */}
        <ProgressRing active={pool.active_agents} max={pool.max_agents} size={32} strokeWidth={3} />

        {/* Pool info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xs text-gray-800">{meta.label}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
              isActive ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
            }`}>
              {pool.active_agents}/{pool.max_agents}
            </span>
            {isThrottled && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                THROTTLED
              </span>
            )}
            {hasFailures && (
              <span className="text-[9px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-medium animate-pulse">
                {pool.failed_last_5min} FAILED
              </span>
            )}
          </div>

          {/* Task description — only when active */}
          {isActive && !pool.bg_agents?.length && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] text-gray-500">{meta.task}</span>
              {/* Animated dots */}
              <span className="inline-flex gap-0.5">
                <span className="w-1 h-1 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_infinite]" />
                <span className="w-1 h-1 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_0.15s_infinite]" style={{ animationDelay: "0.15s" }} />
                <span className="w-1 h-1 rounded-full bg-blue-400 animate-[bounce_1s_ease-in-out_0.3s_infinite]" style={{ animationDelay: "0.3s" }} />
              </span>
            </div>
          )}

          {/* BG agent list — intel pool shows individual background tasks */}
          {pool.bg_agents?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {pool.bg_agents.map((bg) => {
                const bgMeta = BG_TASK_META[bg.agent_id] || { label: bg.agent_id, icon: "\u2699\uFE0F", color: "text-gray-700 bg-gray-50" };
                return (
                  <span
                    key={bg.agent_id}
                    className={`inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-medium ${bgMeta.color}`}
                    title={bg.task_description}
                  >
                    <span>{bgMeta.icon}</span>
                    <span>{bgMeta.label}</span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Queue status bar */}
          {(hasQueue || pool.completed_last_5min > 0) && (
            <div className="mt-1.5 flex items-center gap-2">
              <div className="flex-1">
                <ThroughputBar
                  completed={pool.completed_last_5min}
                  failed={pool.failed_last_5min}
                />
              </div>
              <div className="flex items-center gap-2 text-[10px] flex-shrink-0">
                {pool.completed_last_5min > 0 && (
                  <span className="text-emerald-600 font-medium">
                    {pool.completed_last_5min}/5m
                  </span>
                )}
                {hasQueue && (
                  <span className="text-amber-600">
                    {pool.queue_depth.toLocaleString()} queued
                  </span>
                )}
                {eta && (
                  <span className={`font-medium ${eta === "stalled" ? "text-red-500" : "text-gray-500"}`}>
                    {eta === "stalled" ? "stalled" : `ETA ${eta}`}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Total processed — when idle but has history */}
          {!isActive && totalProcessed > 0 && pool.queue_depth === 0 && pool.completed_last_5min === 0 && (
            <div className="mt-0.5 text-[10px] text-gray-400">
              {totalProcessed.toLocaleString()} processed
              {pool.avg_latency_ms > 0 && (
                <span className="ml-2">
                  avg {pool.avg_latency_ms > 1000
                    ? `${(pool.avg_latency_ms / 1000).toFixed(1)}s`
                    : `${pool.avg_latency_ms}ms`}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Summary bar — overall totals ────────────────────────────────────
function SummaryBar({ totals, pools }) {
  const totalCompleted = Object.values(pools).reduce((s, p) => s + (p.total_completed || 0), 0);
  const totalFailed = Object.values(pools).reduce((s, p) => s + (p.total_failed || 0), 0);
  const activePools = Object.values(pools).filter((p) => p.active_agents > 0).length;
  const totalPools = Object.keys(pools).length;

  return (
    <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[10px]">
      <div className="flex items-center gap-3 text-gray-500">
        <span>{activePools}/{totalPools} pools active</span>
        <span>{totals.active_agents || 0} agents</span>
        <span>{(totals.total_queue_depth || 0).toLocaleString()} queued</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-emerald-600">{totalCompleted.toLocaleString()} done</span>
        {totalFailed > 0 && <span className="text-red-500">{totalFailed} failed</span>}
        <span className="text-gray-400">{totals.completed_last_5min || 0}/5m</span>
      </div>
    </div>
  );
}

// ── Background task row (INTEL-4) ───────────────────────────────────
function BgTaskRow({ agentId, tick }) {
  const meta = BG_TASK_META[agentId] || { label: agentId, icon: "\u2699\uFE0F", color: "text-gray-700 bg-gray-50" };
  const isFailed = tick?.event_type === "tick_failed";
  const isRecent = tick?.created_at && (Date.now() - new Date(tick.created_at).getTime()) < 60000;
  const neverRan = !tick;
  const pct = recencyPct(tick?.created_at);

  return (
    <div className={`px-3 py-2 transition-colors duration-300 ${isRecent && !isFailed ? "bg-blue-50/40" : ""}`}>
      <div className="flex items-center gap-2">
        {/* Status dot */}
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
          neverRan ? "bg-gray-300" : isFailed ? "bg-red-500" : "bg-emerald-500"
        } ${isRecent ? "animate-pulse" : ""}`} />

        {/* Icon + label */}
        <span className="text-xs flex-shrink-0">{meta.icon}</span>
        <span className="font-semibold text-xs text-gray-800 truncate flex-1 min-w-0">{meta.label}</span>

        {/* Duration */}
        <span className="text-[10px] font-mono text-gray-500 flex-shrink-0">
          {formatBgDuration(tick?.latency_ms)}
        </span>

        {/* Time ago */}
        <span className="text-[10px] text-gray-400 flex-shrink-0 w-12 text-right">
          {neverRan ? "never" : bgTimeAgo(tick.created_at)}
        </span>
      </div>

      {/* Description + recency bar */}
      {tick && (
        <div className="ml-[18px] mt-1">
          {isFailed && tick.error_message ? (
            <div className="text-[10px] text-red-500 truncate">{tick.error_message}</div>
          ) : tick.task_description ? (
            <div className="text-[10px] text-gray-500 truncate">{tick.task_description}</div>
          ) : null}
          {/* Recency activity bar */}
          <div className="mt-1 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ease-out ${
                isFailed ? "bg-red-300" : pct > 60 ? "bg-emerald-400" : pct > 20 ? "bg-emerald-300" : "bg-gray-300"
              }`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Background summary bar (INTEL-4) ────────────────────────────────
function BgSummaryBar({ latestTicks }) {
  const total = Object.keys(latestTicks).length;
  const healthy = Object.values(latestTicks).filter((t) => t.event_type === "tick_complete").length;
  const failed = Object.values(latestTicks).filter((t) => t.event_type === "tick_failed").length;
  const neverRan = BG_TASK_ORDER.length - total;

  return (
    <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between text-[10px]">
      <span className="text-gray-500">{total}/11 reporting</span>
      <div className="flex items-center gap-3">
        <span className="text-emerald-600">{healthy} ok</span>
        {failed > 0 && <span className="text-red-500">{failed} failed</span>}
        {neverRan > 0 && <span className="text-gray-400">{neverRan} pending</span>}
      </div>
    </div>
  );
}

export default function AgentStatusWidget({ inSidebar = false, collapsed = false }) {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [widgetTab, setWidgetTab] = useState("pools");
  const [registryAgents, setRegistryAgents] = useState(null);
  const [lastBgTick, setLastBgTick] = useState(null);
  const [bgTasks, setBgTasks] = useState(null);
  const [error, setError] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const abortRef = useRef(null);
  const fetchingRef = useRef(false);
  const btnRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const res = await fetch("/api/agent-status", { signal: controller.signal });
      clearTimeout(timeout);
      if (res.ok) {
        setStatus(await res.json());
        setError(false);
      } else if (res.status === 401) {
        setError(true);
      } else {
        setError(true);
      }
    } catch (e) {
      clearTimeout(timeout);
      if (e.name !== "AbortError") setError(true);
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // Fetch latest background tick for idle label
  useEffect(() => {
    let cancelled = false;
    const fetchBg = async () => {
      try {
        const res = await fetch("/api/agent-status/background-activity?limit=1");
        if (res.ok && !cancelled) {
          const data = await res.json();
          setLastBgTick(data.length > 0 ? data[0] : null);
        }
      } catch { /* silent */ }
    };
    fetchBg();
    const iv = setInterval(fetchBg, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  useEffect(() => {
    fetchStatus();
    const iv = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => {
      clearInterval(iv);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchStatus]);

  // Fetch registry agents when expanded to Registry tab
  useEffect(() => {
    if (!expanded || widgetTab !== "registry" || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/agents/", {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (res.ok && !cancelled) setRegistryAgents(await res.json());
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [expanded, widgetTab, token]);

  // Fetch background task ticks when expanded to Background tab (INTEL-4)
  useEffect(() => {
    if (!expanded || widgetTab !== "background") return;
    let cancelled = false;
    const fetchBgTasks = async () => {
      try {
        const res = await fetch("/api/agent-status/background-activity?limit=50");
        if (res.ok && !cancelled) setBgTasks(await res.json());
      } catch { /* silent */ }
    };
    fetchBgTasks();
    const iv = setInterval(fetchBgTasks, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [expanded, widgetTab]);

  if (error || !status || !status.enabled) return null;

  const color = getStatusColor(status);
  const totals = status.totals || {};
  const pools = status.pools || {};
  const activeCount = totals.active_agents || 0;
  const queueCount = totals.total_queue_depth || 0;

  // ── Sidebar collapsed: just a colored dot ────────────────────────
  if (inSidebar && collapsed) {
    return (
      <div className="flex justify-center py-1" title={`Agents: ${activeCount} active, ${queueCount} queued`}>
        <span
          className={`w-3 h-3 rounded-full ${COLOR_CLASSES[color]} ${
            activeCount > 0 ? "animate-pulse" : ""
          }`}
        />
      </div>
    );
  }

  // ── Expanded panel content (shared between sidebar & fixed) ──────
  const panelContent = (
    <>
      {/* Header with tabs */}
      <div className="px-3 py-2 bg-gray-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${COLOR_CLASSES[color]} ${activeCount > 0 ? "animate-pulse" : ""}`} />
          <button
            onClick={() => setWidgetTab("pools")}
            className={`text-xs font-semibold tracking-wide pb-0.5 border-b-2 transition-colors ${
              widgetTab === "pools" ? "border-blue-400 text-white" : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            POOLS
          </button>
          <button
            onClick={() => setWidgetTab("registry")}
            className={`text-xs font-semibold tracking-wide pb-0.5 border-b-2 transition-colors ${
              widgetTab === "registry" ? "border-blue-400 text-white" : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            REGISTRY
          </button>
          <button
            onClick={() => setWidgetTab("background")}
            className={`text-xs font-semibold tracking-wide pb-0.5 border-b-2 transition-colors ${
              widgetTab === "background" ? "border-blue-400 text-white" : "border-transparent text-gray-400 hover:text-gray-200"
            }`}
          >
            BACKGROUND
          </button>
        </div>
        {widgetTab === "pools" && (
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className={activeCount > 0 ? "text-blue-300" : ""}>{activeCount} active</span>
            <span>{queueCount.toLocaleString()} queued</span>
          </div>
        )}
        {widgetTab === "registry" && registryAgents && (
          <div className="text-[10px] text-gray-400">
            {registryAgents.length} agent{registryAgents.length !== 1 ? "s" : ""}
          </div>
        )}
        {widgetTab === "background" && bgTasks && (
          <div className="text-[10px] text-gray-400">
            {Object.keys(groupByLatestTick(bgTasks)).length}/11 tasks
          </div>
        )}
      </div>

      {widgetTab === "pools" && (
        <>
          <div className="divide-y divide-gray-100">
            {POOL_ORDER.map((poolName) => {
              const pool = pools[poolName];
              if (!pool) return null;
              return <PoolRow key={poolName} poolName={poolName} pool={pool} />;
            })}
          </div>
          <SummaryBar totals={totals} pools={pools} />
        </>
      )}

      {widgetTab === "registry" && (
        <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
          {registryAgents ? registryAgents.map((agent) => (
            <div key={agent.id} className="px-3 py-2 flex items-center gap-2 text-xs">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                agent.circuit_breaker ? (CB_DOT[agent.circuit_breaker.state] || "bg-gray-300") : "bg-gray-300"
              }`} title={agent.circuit_breaker ? `CB: ${agent.circuit_breaker.state}` : ""} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-gray-800 truncate">{agent.name}</span>
                  <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
                    agent.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"
                  }`}>
                    {agent.is_active ? "ON" : "OFF"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 mt-0.5">
                  {agent.capability_entries?.map((cap) => (
                    <span
                      key={cap.id}
                      className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                        TRUST_COLORS[cap.trust_level] || TRUST_COLORS.draft
                      }`}
                    >
                      {cap.capability.replace(/_/g, " ")}
                    </span>
                  ))}
                </div>
              </div>
              <span className="text-[10px] text-gray-400 flex-shrink-0">v{agent.version}</span>
            </div>
          )) : (
            <div className="px-3 py-4 text-center text-gray-400 text-xs">Loading registry...</div>
          )}
          {registryAgents?.length === 0 && (
            <div className="px-3 py-4 text-center text-gray-400 text-xs">No agents registered</div>
          )}
        </div>
      )}

      {widgetTab === "background" && (
        <>
          <div className="divide-y divide-gray-100 max-h-[350px] overflow-y-auto">
            {bgTasks ? (
              (() => {
                const latestTicks = groupByLatestTick(bgTasks);
                return BG_TASK_ORDER.map((agentId) => (
                  <BgTaskRow key={agentId} agentId={agentId} tick={latestTicks[agentId] || null} />
                ));
              })()
            ) : (
              <div className="px-3 py-8 text-center text-gray-400">
                <div className="text-2xl mb-2">{"\u{1F4E1}"}</div>
                <div className="text-xs font-medium">Loading background tasks...</div>
              </div>
            )}
          </div>
          {bgTasks && <BgSummaryBar latestTicks={groupByLatestTick(bgTasks)} />}
        </>
      )}
    </>
  );

  // ── Sidebar expanded: compact inline widget ──────────────────────
  // Panel rendered via portal to escape sidebar overflow-hidden.
  // Position is computed from button ref on each expand toggle.
  if (inSidebar && !collapsed) {
    const handleToggle = () => {
      if (!expanded && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        setPanelPos({ bottom: window.innerHeight - rect.top + 4, left: rect.right + 8 });
      }
      setExpanded(!expanded);
    };

    return (
      <div>
        <button
          ref={btnRef}
          onClick={handleToggle}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-neutral-600 hover:bg-neutral-100 dark:text-gray-300 dark:hover:bg-gray-800 transition-colors"
          title={`Agent Pools: ${activeCount} active, ${queueCount.toLocaleString()} queued`}
        >
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${COLOR_CLASSES[color]} ${
              activeCount > 0 ? "animate-pulse" : ""
            }`}
          />
          <span className="truncate">
            {activeCount > 0
              ? `${activeCount} agent${activeCount !== 1 ? "s" : ""} working`
              : lastBgTick
                ? `${BG_TASK_LABELS[lastBgTick.agent_id] || lastBgTick.agent_id} — ${bgTimeAgo(lastBgTick.created_at)}`
                : "Agents idle"}
          </span>
          {queueCount > 0 && (
            <span className="text-[10px] text-neutral-500 dark:text-gray-500">+{queueCount.toLocaleString()}q</span>
          )}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`h-3 w-3 ml-auto flex-shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>

        {expanded && panelPos && createPortal(
          <>
            {/* Backdrop to close on outside click */}
            <div className="fixed inset-0 z-[69]" onClick={() => setExpanded(false)} />
            <div
              className="fixed bg-white rounded-xl shadow-sm border border-neutral-200/90 w-[440px] overflow-hidden z-[70]"
              style={{ bottom: panelPos.bottom, left: panelPos.left }}
            >
              {panelContent}
            </div>
          </>,
          document.body
        )}
      </div>
    );
  }

  // ── Default: fixed-position widget (fallback / non-sidebar) ──────
  return (
    <div className="fixed bottom-4 right-4 z-50">
      {expanded && (
        <div className="mb-2 bg-white rounded-xl shadow-sm border border-neutral-200/90 w-[440px] overflow-hidden">
          {panelContent}
        </div>
      )}

      {/* Collapsed pill */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm hover:shadow-md transition-all text-xs font-medium ${PILL_CLASSES[color]}`}
        title={expanded ? "Collapse agent status" : `Agent Pools: ${activeCount} active, ${queueCount.toLocaleString()} queued`}
      >
        <span className={`w-2 h-2 rounded-full ${COLOR_CLASSES[color]} ${activeCount > 0 ? "animate-pulse" : ""}`} />
        <span>
          {activeCount > 0
            ? `${activeCount} agent${activeCount !== 1 ? "s" : ""} working`
            : lastBgTick
              ? `${BG_TASK_LABELS[lastBgTick.agent_id] || lastBgTick.agent_id} — ${bgTimeAgo(lastBgTick.created_at)}`
              : "Agents idle"}
        </span>
        {queueCount > 0 && (
          <span className="text-[10px] opacity-70">+{queueCount.toLocaleString()}q</span>
        )}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>
    </div>
  );
}
